import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getOuttaStockListings, getListing } from "actions/listingsActions";
import { useHistory } from "react-router-dom";
import { getUserFromLocalStorage } from "utilities/user";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:         "#f4f6fb",
  panel:      "#ffffff",
  header:     "#1a2b4a",
  red:        "#e63757",
  redSoft:    "#fff0f3",
  redMid:     "#f5c0cc",
  orange:     "#f6961e",
  orangeSoft: "#fff4e6",
  teal:       "#00b69b",
  tealSoft:   "#e6faf7",
  blue:       "#2c7be5",
  blueSoft:   "#eaf2ff",
  text:       "#1a2b4a",
  textMid:    "#4a5568",
  textLight:  "#8898aa",
  border:     "#e2e8f0",
  shadow:     "0 2px 12px rgba(26,43,74,0.08)",
  radius:     "10px",
  radiusSm:   "6px",
  radiusXs:   "4px",
};

// ── Skeleton row ──────────────────────────────────────────────────────────────
const SkeletonRow = () => (
  <tr>
    {[60, 90, 70, 55, 60, 80].map((w, i) => (
      <td key={i} style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{
          height: i === 0 ? 38 : 14,
          width: i === 0 ? 180 : w,
          borderRadius: C.radiusSm,
          background: "linear-gradient(90deg,#e8edf5 25%,#f4f6fb 50%,#e8edf5 75%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.4s infinite",
        }} />
      </td>
    ))}
  </tr>
);

const OutOfStock = () => {
  const user    = getUserFromLocalStorage();
  const listings = useSelector((state) => state.listings);
  const { isLoading, outtaStock } = listings;
  const dispatch = useDispatch();
  const history  = useHistory();

  useEffect(() => {
    if (user?.result?._id) {
      dispatch(getOuttaStockListings(user?.result?._id));
    }
  }, [dispatch, user?.result?._id]);

  const items = outtaStock?.data || [];
  const count = items.length;

  const th = {
    padding: "10px 16px", textAlign: "left",
    fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
    textTransform: "uppercase", color: C.textLight,
    borderBottom: `2px solid ${C.border}`, background: C.panel,
    whiteSpace: "nowrap",
  };
  const td = {
    padding: "13px 16px", borderBottom: `1px solid ${C.border}`,
    color: C.textMid, verticalAlign: "middle", fontSize: 13,
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "28px 16px 48px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>

        {/* ── Header ── */}
        <div style={{
          background: `linear-gradient(135deg, ${C.header} 0%, #243b6e 100%)`,
          borderRadius: C.radius, padding: "22px 28px", marginBottom: 24,
          boxShadow: "0 4px 24px rgba(26,43,74,0.18)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: C.radiusSm, flexShrink: 0,
              background: "rgba(230,55,87,0.22)", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="fas fa-box-open" style={{ color: C.red, fontSize: 18 }} />
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#fff", letterSpacing: "-0.3px", marginBottom: 2 }}>
                Out of Stock Products
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }}>
                Products that need restocking in your shop
              </div>
            </div>
          </div>

          {/* Count badge */}
          {!isLoading && (
            <div style={{
              background: count > 0 ? "rgba(230,55,87,0.18)" : "rgba(0,182,155,0.18)",
              border: `1px solid ${count > 0 ? "rgba(230,55,87,0.4)" : "rgba(0,182,155,0.4)"}`,
              borderRadius: 20, padding: "6px 16px",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: "50%",
                background: count > 0 ? C.red : C.teal,
                display: "inline-block",
                animation: count > 0 ? "pulse 1.8s infinite" : "none",
              }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: count > 0 ? "#ffb3bf" : "#7eeee4" }}>
                {count} {count === 1 ? "product" : "products"} need{count === 1 ? "s" : ""} restocking
              </span>
            </div>
          )}
        </div>

        {/* ── Content panel ── */}
        <div style={{
          background: C.panel, borderRadius: C.radius,
          boxShadow: C.shadow, border: `1px solid ${C.border}`, overflow: "hidden",
        }}>

          {/* Loading skeleton */}
          {isLoading && (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Product", "Price", "Status", "Stock", "Verification", "Action"].map((h) => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4].map((i) => <SkeletonRow key={i} />)}
                </tbody>
              </table>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && count === 0 && (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", padding: "64px 24px",
            }}>
              <div style={{
                width: 72, height: 72, borderRadius: "50%",
                background: C.tealSoft, display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 18,
              }}>
                <i className="fas fa-check-circle" style={{ color: C.teal, fontSize: 32 }} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6 }}>
                All products are in stock
              </div>
              <div style={{ fontSize: 13, color: C.textLight, maxWidth: 320, textAlign: "center" }}>
                Great work! None of your listings are currently out of stock.
              </div>
            </div>
          )}

          {/* Table */}
          {!isLoading && count > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={th}>Product</th>
                    <th style={th}>Price</th>
                    <th style={th}>Status</th>
                    <th style={{ ...th, textAlign: "center" }}>Stock</th>
                    <th style={{ ...th, textAlign: "center" }}>Verification</th>
                    <th style={{ ...th, textAlign: "center" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((listing) => (
                    <tr
                      key={listing._id}
                      style={{ transition: "background 0.12s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = C.bg)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      {/* Product */}
                      <td style={{ ...td, minWidth: 220 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{
                            width: 46, height: 46, borderRadius: C.radiusSm, flexShrink: 0,
                            overflow: "hidden", border: `1px solid ${C.border}`,
                            background: C.bg,
                          }}>
                            {listing.product_image ? (
                              <img
                                src={listing.product_image}
                                alt={listing.title}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            ) : (
                              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <i className="fas fa-image" style={{ color: C.textLight, fontSize: 16 }} />
                              </div>
                            )}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, color: C.text, fontSize: 13, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>
                              {listing.title}
                            </div>
                            <div
                              style={{ fontSize: 11, color: C.textLight, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" }}
                              dangerouslySetInnerHTML={{ __html: listing.description }}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Price */}
                      <td style={td}>
                        <span style={{ fontWeight: 700, color: C.text }}>
                          UGX {listing.price?.toLocaleString() ?? "—"}
                        </span>
                      </td>

                      {/* Status pill */}
                      <td style={td}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          padding: "3px 10px", borderRadius: 20,
                          fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
                          background: C.redSoft, color: C.red,
                          border: `1px solid ${C.redMid}`,
                        }}>
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.red, display: "inline-block" }} />
                          Out of Stock
                        </span>
                      </td>

                      {/* Stock count with bar */}
                      <td style={{ ...td, textAlign: "center" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                          <span style={{
                            fontSize: 15, fontWeight: 800,
                            color: listing.countInStock === 0 ? C.red : C.orange,
                          }}>
                            {listing.countInStock}
                          </span>
                          <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border }}>
                            <div style={{
                              width: listing.countInStock === 0 ? "0%" : "20%",
                              height: "100%", borderRadius: 2,
                              background: C.orange,
                            }} />
                          </div>
                          <span style={{ fontSize: 10, color: C.textLight }}>units</span>
                        </div>
                      </td>

                      {/* Verification */}
                      <td style={{ ...td, textAlign: "center" }}>
                        {listing?.verifyListing === "VERIFIED" ? (
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            padding: "3px 9px", borderRadius: 20,
                            fontSize: 11, fontWeight: 700,
                            background: C.blueSoft, color: C.blue,
                          }}>
                            <i className="fas fa-shield-alt" style={{ fontSize: 10 }} />
                            Verified
                          </span>
                        ) : (
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            padding: "3px 9px", borderRadius: 20,
                            fontSize: 11, fontWeight: 700,
                            background: C.orangeSoft, color: C.orange,
                          }}>
                            <i className="fas fa-clock" style={{ fontSize: 10 }} />
                            Pending
                          </span>
                        )}
                      </td>

                      {/* Action */}
                      <td style={{ ...td, textAlign: "center" }}>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            dispatch(getListing(listing._id, history.push(`/admin/shopEdit/${listing?._id}`)));
                          }}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            padding: "7px 16px", borderRadius: C.radiusSm,
                            border: "none", cursor: "pointer",
                            background: `linear-gradient(135deg, #1a2b4a 0%, #2c4a7e 100%)`,
                            color: "#fff", fontSize: 12, fontWeight: 700,
                            boxShadow: "0 2px 8px rgba(26,43,74,0.25)",
                            transition: "opacity 0.15s, transform 0.1s",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(0)"; }}
                        >
                          <i className="fas fa-plus-circle" style={{ fontSize: 11 }} />
                          Restock
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          {!isLoading && count > 0 && (
            <div style={{
              borderTop: `1px solid ${C.border}`, padding: "10px 16px",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              fontSize: 11, color: C.textLight,
            }}>
              <span>{count} item{count !== 1 ? "s" : ""} out of stock</span>
              <span style={{ color: C.red, fontWeight: 600 }}>
                <i className="fas fa-exclamation-circle mr-1" style={{ fontSize: 10 }} />
                Click Restock to update quantity
              </span>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
};

export default OutOfStock;
